import React, { useContext } from 'react'
import { MappingContext, MappingContextType } from '../Contexts/MappingContext';
import { Cross } from 'Components/DesignSystem/Iconography/Icons';

const SelectedFilters = () => {
    const { getActiveFilterChips, clearFilter, clearAllFilters, activeTab } = useContext<MappingContextType>(MappingContext);

    // Get filters for current tab only
    const activeFilters = getActiveFilterChips(activeTab);

    const handleClearFilter = (filterId: string) => {
        clearFilter(filterId);
    };

    const handleClearAll = () => {
        clearAllFilters();
    };

    // Group filters by category
    const groupedFilters = activeFilters.reduce((acc, filter) => {
        let category = '';

        if (filter.category) {
            category = filter.category;
        } else if (filter.filterKey === 'consider') {
            category = 'Consider';
        } else if (filter.filterKey === 'review') {
            category = 'Review';
        }

        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(filter);
        return acc;
    }, {} as Record<string, typeof activeFilters>);

    // Render individual chip. The clear button is templated per chip id
    // (2026-09-05) — the old `components-selectedfilters-button-1` was one
    // string duplicated across every chip, unusable the moment two rendered.
    // TODO(design-system): a chip-clear affordance this small (20px inside a
    // 24px chip) has no DS IconButton size — the smallest is sm/28px — so this
    // stays a native button wearing the DS Cross glyph and role tokens.
    const renderChip = (filter: any) => (
        <div
            key={filter.id}
            className="tw-h-6 tw-px-2 tw-py-1 tw-bg-surface tw-rounded tw-outline tw-outline-1 tw-outline-offset-[-1px] tw-outline-edge tw-inline-flex tw-justify-start tw-items-center tw-gap-1"
            data-testid={`filter-chip-${filter.id}`}
        >
            <div className="tw-justify-start tw-text-ink tw-text-xs tw-font-medium tw-leading-tight">
                {filter.label}
            </div>
            <button data-testid={`filter-chip-clear-${filter.id}`}
                onClick={() => handleClearFilter(filter.id)}
                className="tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center hover:tw-bg-surface-hover tw-rounded tw-text-ink-secondary hover:tw-text-ink"
                title="Remove filter"
                aria-label="Remove filter"
            >
                <Cross size={12} />
            </button>
        </div>
    );

    if (activeFilters.length === 0) {
        return null;
    }

    return (
        <div
            className="tw-w-full tw-p-2 tw-bg-surface-sunken tw-rounded tw-inline-flex tw-flex-col tw-justify-start tw-items-start tw-gap-2.5"
            data-testid="filter-chips"
        >
            {/* First Row - Tab-specific and Review filters */}
            <div className="tw-inline-flex tw-justify-start tw-items-center tw-gap-2 tw-flex-wrap">
                {/* Tab-specific filters based on activeTab */}
                {activeTab === 'attribute' && (
                    <>
                        {/* Incoming Attributes */}
                        {groupedFilters['Incoming Attributes'] && (
                            <div className="tw-flex tw-justify-start tw-items-center tw-gap-3">
                                <div className="tw-flex tw-justify-start tw-items-center tw-gap-1">
                                    <div className="tw-justify-start tw-text-ink-tertiary tw-text-xs tw-font-medium tw-leading-none">
                                        Incoming Attributes
                                    </div>
                                    {groupedFilters['Incoming Attributes'].map(renderChip)}
                                </div>
                            </div>
                        )}

                        {/* Mapped Attributes */}
                        {groupedFilters['Mapped Attributes'] && (
                            <div className="tw-flex tw-justify-start tw-items-center tw-gap-3">
                                <div className="tw-flex tw-justify-start tw-items-center tw-gap-1">
                                    <div className="tw-justify-start tw-text-ink-tertiary tw-text-xs tw-font-medium tw-leading-none">
                                        Mapped Attributes
                                    </div>
                                    {groupedFilters['Mapped Attributes'].map(renderChip)}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'attributevalue' && (
                    <>
                        {/* Incoming Attribute Values */}
                        {groupedFilters['Incoming Attributes Values'] && (
                            <div className="tw-flex tw-justify-start tw-items-center tw-gap-3">
                                <div className="tw-flex tw-justify-start tw-items-center tw-gap-1">
                                    <div className="tw-justify-start tw-text-ink-tertiary tw-text-xs tw-font-medium tw-leading-none">
                                        Incoming Attributes Values
                                    </div>
                                    {groupedFilters['Incoming Attributes Values'].map(renderChip)}
                                </div>
                            </div>
                        )}

                        {/* Mapped Attribute Values */}
                        {groupedFilters['Mapped Attributes Values'] && (
                            <div className="tw-flex tw-justify-start tw-items-center tw-gap-3">
                                <div className="tw-flex tw-justify-start tw-items-center tw-gap-1">
                                    <div className="tw-justify-start tw-text-ink-tertiary tw-text-xs tw-font-medium tw-leading-none">
                                        Mapped Attributes Values
                                    </div>
                                    {groupedFilters['Mapped Attributes Values'].map(renderChip)}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {activeTab === 'category' && (
                    <>
                        {/* Reporting Categories */}
                        {groupedFilters['Reporting Categories'] && (
                            <div className="tw-flex tw-justify-start tw-items-center tw-gap-3">
                                <div className="tw-flex tw-justify-start tw-items-center tw-gap-1">
                                    <div className="tw-justify-start tw-text-ink-tertiary tw-text-xs tw-font-medium tw-leading-none">
                                        Reporting Categories
                                    </div>
                                    {groupedFilters['Reporting Categories'].map(renderChip)}
                                </div>
                            </div>
                        )}

                        {/* Mapped Categories */}
                        {groupedFilters['Mapped Categories'] && (
                            <div className="tw-flex tw-justify-start tw-items-center tw-gap-3">
                                <div className="tw-flex tw-justify-start tw-items-center tw-gap-1">
                                    <div className="tw-justify-start tw-text-ink-tertiary tw-text-xs tw-font-medium tw-leading-none">
                                        Mapped Categories
                                    </div>
                                    {groupedFilters['Mapped Categories'].map(renderChip)}
                                </div>
                            </div>
                        )}
                    </>
                )}

                {/* Review filters */}
                {groupedFilters['Review'] && (
                    <div className="tw-flex tw-justify-start tw-items-center tw-gap-3">
                        <div className="tw-flex tw-justify-start tw-items-center tw-gap-1">
                            <div className="tw-justify-start tw-text-ink-tertiary tw-text-xs tw-font-medium tw-leading-none">Review</div>
                            {groupedFilters['Review'].map(renderChip)}
                        </div>
                    </div>
                )}

                {/* Consider filters */}
                {groupedFilters['Consider'] && (
                    <div className="tw-flex tw-justify-start tw-items-center tw-gap-3">
                        <div className="tw-flex tw-justify-start tw-items-center tw-gap-1">
                            <div className="tw-justify-start tw-text-ink tw-text-xs tw-font-medium tw-leading-none">Consider</div>
                            {groupedFilters['Consider'].map(renderChip)}
                        </div>
                    </div>
                )}

                {/* clear All */}
                <button
                    onClick={handleClearAll}
                    className="tw-justify-start tw-text-accent tw-text-sm tw-font-normal tw-leading-tight hover:tw-underline"
                    data-testid="clear-all-filters"
                >
                    Clear All
                </button>
            </div>
        </div>
    );
};

export default SelectedFilters;